import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './Components/Sidebar';
import Navbar from './Components/Navbar';
import Footer from './Components/Footer';
import BottomNav from './Components/BottomNav';
import Dashboard from './Pages/Dashboard';
import TeacherLogin from './Pages/Login';
import ProfileManagement from './Pages/ProfileManagement';
import CoursesManagement from './Pages/CoursesManagement';
import Examination from './Pages/Examination';
import FeeManagement from './Pages/FeeManagement';
import NotificationManagement from './Pages/NotificationManagement';

import UserPassword from './Dashboard/UserPassword';
import ViewProfile from './Profile/ViewProfile';
import Attendance from './Profile/Attendance';
import Schedule from './Profile/Schedule';
import Assignments from './Courses/Assignments';
import CourseMaterial from './Courses/CourseMaterial';
import Homework from './Courses/Homework';
import HomeworkNotCompleted from './Courses/HomeworkNotCompleted';
import MyClass from "./Courses/MyClass";
import StudentAttendance from './Courses/StudentAttendance';
import TestSchedule from './Courses/TestSchedule';
import OldPaper from './Examination/OldPaper';
import PrepareResult from './Examination/PrepareResult';
import Result from './Examination/Result';
import Ledger from './Salary/Ledger';
import ViewSalary from './Salary/ViewSalary';
import FeeReport from './Dashboard/FeeReport';
import ClassReport from './Dashboard/ClassReports';
import AddNotification from './Notification/AddNotification';
import StudentAchievement from './Notification/StudentAchievement';
import ViewNotification from './Notification/ViewNotification';

const LayoutContent = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/';

  // Check token on mount and listen for storage changes
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsAuthenticated(!!token);
      setLoading(false);
    };

    checkAuth();

    // Listen for storage events (triggered from login)
    window.addEventListener('storage', checkAuth);
    
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-t-transparent border-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Redirect logic
  if (!isAuthenticated && !isLoginPage) {
    return <Navigate to="/login" replace />;
  }
  
  if (isAuthenticated && isLoginPage) {
    return <Navigate to="/dashboard" replace />;
  }

  // Render login page without layout components
  if (isLoginPage) {
    return (
      <Routes>
        <Route path="/login" element={<TeacherLogin />} />
        <Route path="/" element={<TeacherLogin />} />
      </Routes>
    );
  }

  // Render protected pages with full layout
  return (
    <div className="layout-container flex flex-col min-h-screen">
      {/* Navbar */}
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="flex flex-1 pt-16 md:pt-0">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'block' : 'hidden'} md:block`}>
          <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        </div>

        {/* Main Content */}
        <div className="main-content flex-1 p-4 w-full overflow-auto">
          <Routes>
            {/* Protected Routes */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile-management" element={<ProfileManagement />} />
            <Route path="/courses-management" element={<CoursesManagement />} />
            <Route path="/examination-management" element={<Examination />} />
            <Route path="/fee-management" element={<FeeManagement />} />
            <Route path="/notification-management" element={<NotificationManagement />} />
            <Route path="/user-password" element={<UserPassword />} />
            <Route path="/view-profile" element={<ViewProfile />} />
            <Route path="/view-attendance" element={<Attendance />} />
            <Route path="/my-schedule" element={<Schedule />} />
            <Route path="/assignments" element={<Assignments />} />
            <Route path="/course-materials" element={<CourseMaterial />} />
            <Route path="/homework" element={<Homework />} />
            <Route path="/homeworkNotCompleted" element={<HomeworkNotCompleted />} />
            <Route path="/my-class" element={<MyClass />}/>
            <Route path="/student-attendance" element={<StudentAttendance />} />
            <Route path="/test-schedule" element={<TestSchedule />} />
            <Route path="/old-papers" element={<OldPaper />} />
            <Route path="/prepare-result" element={<PrepareResult />} />
            <Route path="/submit-result" element={<Result />} />
            <Route path="/view-ledger" element={<Ledger />} />
            <Route path="/view-salary" element={<ViewSalary />} />
            <Route path="/fee-report" element={<FeeReport />} />
            <Route path="/class-reports" element={<ClassReport />} />
            <Route path="/add-notification" element={<AddNotification />} />
            <Route path="/student-achievements" element={<StudentAchievement />} />
            <Route path="/view-notification" element={<ViewNotification />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Bottom Navigation for mobile */}
      <div className="md:hidden fixed bottom-0 w-full z-50">
        <BottomNav />
      </div>
    </div>
  );
};

const Layout = () => (
  <Router>
    <LayoutContent />
  </Router>
);

export default Layout;